/**
 * @fileoverview Hook for managing import source tracking.
 *
 * Provides functionality to:
 * - Track the active import source (pasted text, uploaded file, or selected resource)
 * - Auto-save pasted text as a resource file
 * - Detect when the import source has changed
 * - Clear imported data when switching to a different source
 */

'use client';

import { useCallback, useMemo } from 'react';
import { useProject } from '@/context/project-context';
import { useResources } from './use-resources';
import { RESOURCE_CATEGORIES } from '../constants';
import type { Resource } from '../schemas';
import { authFetch } from '@/lib/auth-fetch';

export type ImportSourceType = 'pasted' | 'uploaded' | 'selected';

export interface ImportSource {
  resourceId: string;
  fileName: string;
  createdAt: string;
  sourceType: ImportSourceType;
}

interface SaveTextAsResourceOptions {
  /** Override the generated filename */
  fileName?: string;
  /** Source type to record */
  sourceType?: ImportSourceType;
}

/**
 * Generates a filename for auto-saved import text.
 */
function generateImportFileName(projectTitle: string): string {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 16).replace('T', ' ').replace(':', '-');
  const sanitizedTitle = (projectTitle || 'Import').slice(0, 30).replace(/[^a-zA-Z0-9\s-]/g, '');
  return `Import - ${sanitizedTitle} - ${dateStr}.txt`;
}

/**
 * Clears all persisted import data: importSource, importCompletedSteps, and localStorage text.
 * Shared by the standalone "Clear Import" action and resource-deletion propagation.
 */
export async function clearImportPersistedData(
  projectId: string,
  updateProjectCore: (updates: Record<string, unknown>) => Promise<void>
): Promise<void> {
  await updateProjectCore({ importSource: null, importCompletedSteps: [] });
  try {
    localStorage.removeItem(`import-text-${projectId}`);
  } catch {
    // localStorage may not be available (SSR, quota issues)
  }
}

export function useImportSource() {
  const { project, updateProjectCore } = useProject();
  const { resources, addResource } = useResources();

  const importSource = project.importSource || null;

  /**
   * Get the resource object for the current import source.
   */
  const sourceResource = useMemo(() => {
    if (!importSource?.resourceId) return null;
    return resources.find((r) => r.id === importSource.resourceId) || null;
  }, [importSource?.resourceId, resources]);

  /**
   * Save text content as a resource and set it as the active import source.
   * This is used for auto-saving pasted text.
   */
  const saveTextAsResource = useCallback(
    async (
      text: string,
      options: SaveTextAsResourceOptions = {}
    ): Promise<Resource> => {
      const { fileName, sourceType = 'pasted' } = options;
      const finalFileName = fileName || generateImportFileName(project.title);

      // Create a text file blob
      const blob = new Blob([text], { type: 'text/plain' });
      const file = new File([blob], finalFileName, { type: 'text/plain' });

      // Upload the file via the API
      const formData = new FormData();
      formData.append('file', file);

      const response = await authFetch(`/api/projects/${project.id}/resources`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save import text');
      }

      const responseData = await response.json();

      // Create the resource record
      const newResource: Resource = {
        id: crypto.randomUUID(),
        fileName: finalFileName,
        fileType: 'text/plain',
        fileSize: blob.size,
        path: responseData.filePath,
        category: RESOURCE_CATEGORIES.CANON,
      };

      await addResource(newResource);

      // Set as the active import source
      const newImportSource: ImportSource = {
        resourceId: newResource.id,
        fileName: finalFileName,
        createdAt: new Date().toISOString(),
        sourceType,
      };

      await updateProjectCore({ importSource: newImportSource });

      return newResource;
    },
    [project.id, project.title, addResource, updateProjectCore]
  );

  /**
   * Set an existing resource as the active import source.
   */
  const setImportSource = useCallback(
    async (resource: Resource, sourceType: ImportSourceType = 'selected') => {
      const newImportSource: ImportSource = {
        resourceId: resource.id,
        fileName: resource.fileName,
        createdAt: new Date().toISOString(),
        sourceType,
      };

      await updateProjectCore({ importSource: newImportSource });
    },
    [updateProjectCore]
  );

  /**
   * Clear the active import source.
   */
  const clearImportSource = useCallback(async () => {
    await updateProjectCore({ importSource: null });
  }, [updateProjectCore]);

  /**
   * Clear all import data: importSource, completed steps, and localStorage text.
   * Use this for a full reset (e.g., "Clear Import" button or resource deletion propagation).
   */
  const clearAllImportData = useCallback(async () => {
    await clearImportPersistedData(project.id, updateProjectCore);
  }, [project.id, updateProjectCore]);

  /**
   * Check if a given resource ID matches the current import source.
   * Used by import hooks to determine if data should be preserved.
   */
  const isSameSource = useCallback(
    (resourceId: string | null | undefined): boolean => {
      if (!resourceId || !importSource?.resourceId) return false;
      return resourceId === importSource.resourceId;
    },
    [importSource?.resourceId]
  );

  /**
   * Check if we have existing imported data that might be affected by a source change.
   */
  const hasImportedData = useMemo(() => {
    const hasCharacters = (project.characters_v2?.length || 0) > 0;
    const hasChapters = (project.chapters?.length || 0) > 0;
    const hasPlot = !!project.storyStructure?.summary || !!project.storyStructure?.writtenPlot;
    const hasStyleGuides = (project.styleGuides?.length || 0) > 0;

    return hasCharacters || hasChapters || hasPlot || hasStyleGuides;
  }, [
    project.characters_v2?.length,
    project.chapters?.length,
    project.storyStructure?.summary,
    project.storyStructure?.writtenPlot,
    project.styleGuides?.length,
  ]);

  /**
   * Determine if switching to a new source would require clearing existing data.
   * Returns true if there's existing imported data AND the new source is different.
   */
  const wouldRequireDataClear = useCallback(
    (newResourceId: string | null | undefined): boolean => {
      // No new source specified - no clear needed
      if (!newResourceId) return false;
      // No current source - no clear needed (fresh import)
      if (!importSource?.resourceId) return false;
      // Same source - no clear needed
      if (newResourceId === importSource.resourceId) return false;
      // Different source with existing data - clear needed
      return hasImportedData;
    },
    [importSource?.resourceId, hasImportedData]
  );

  return {
    /** The current import source, if any */
    importSource,
    /** The resource object for the current import source */
    sourceResource,
    /** Whether we have any imported data */
    hasImportedData,
    /** Save text content as a resource and set as import source */
    saveTextAsResource,
    /** Set an existing resource as the import source */
    setImportSource,
    /** Clear the import source */
    clearImportSource,
    /** Clear all import data (importSource, completed steps, localStorage text) */
    clearAllImportData,
    /** Check if a resource ID matches the current import source */
    isSameSource,
    /** Check if switching to a new source would require clearing data */
    wouldRequireDataClear,
  };
}
